import axios from "axios";
import {METHOD_TYPE} from "@/common/utils/constants";
import router from "@/common/router";
import * as CONFIDENTIALITY_SERVICE from "@/drc/services/confidentiality-service";

const URL = process.env.VUE_APP_BACKEND_API_URL;
const VERSION = process.env.VUE_APP_BACKEND_API_URL_VERSION;

export const GRAPHQL_ENDPOINT = `${process.env.VUE_APP_BACKEND_API_URL}/graphql`;

// ----- Time Off Services

export function fetchActiveTimeOff() {
	return axios.get(`${URL}/old-management/time-offs/get-all-time-offs`);
}

export function setDaysOff(data) {
	return axios.post(`${URL}/old-management/time-offs/set-time-off`, data);
}

export function deleteVacation(id) {
	return axios.delete(`${URL}/old-management/time-offs/remove-time-off?id=${id}`);
}

export function updateVacation(data) {
	return axios.patch(`${URL}/old-management/time-offs/modify-existing-time-off`, data);
}

// ------ Interactive Link Services

export function getIndustriesForSideMenu() {
	return axios.get(`${URL}/old-industries/side-menu`);
}

export function getServiceLinesForSideMenu() {
	return axios.get(`${URL}/old-service-lines/side-menu`);
}

export function getTechPracticesForSideMenu() {
	return axios.get(`${URL}/old-tech-practices/side-menu`);
}

// ------ Management Content Services

export function deleteAttribute(id) {
	return axios.delete(`${URL}/old-management/attributes/${id}/delete`);
}

export function saveQualificationAttribute(data) {
	return axios.post(`${URL}/old-management/attributes/create`, data);
}

export function updateQualificationAttribute(data) {
	return axios.patch(`${URL}/old-management/attributes/update`, data);
}

export function searchQualificationAttributes(params) {
	return axios.get(`${URL}/old-management/attributes/search${params}`);
}

// ------ Categories Services

export function industries() {
	return axios.get(`${URL}/old-industries/get-all-with-count`);
}

export function serviceLines() {
	return axios.get(`${URL}/old-service-lines/get-all-with-count`);
}

export function techPractices() {
	return axios.get(`${URL}/old-tech-practices/get-all-with-count`);
}

export function fetchSubmittedProjectsOption(userId) {
	return axios.get(`${URL}/promote-options/submitted-projects?userId=${userId}`);
}

export function getNewStatistics(criteria) {
	return axios.post(`${URL}/statistics/search`, criteria);
}

export function fetchTranslationValidationsOption(userId) {
	return axios.get(`${URL}/promote-options/validated-translations?userId=${userId}`);
}

export function fetchFilledSurveyOption(userId) {
	return axios.get(`${URL}/promote-options/filled-surveys?userId=${userId}`);
}

export function searchPromotedProjects(criteria) {
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
					getPromotions(
						criteria: {
								pageable: { page: ${criteria.pageable.page}, size: ${criteria.pageable.size} }
								sort: { by: "${criteria.sort.by}", order: "${criteria.sort.order}" }
								${criteria.userId ? `promoterIds: [${criteria.userId}]` : ''}
								${criteria.projectIds ? `projectIds: [${criteria.projectIds}]` : ''}
								${criteria.isActive ? `isActive: ${criteria.isActive}` : ''}
						}
					) {
						id
						displayOrder
						createdDate
						expirationDate
						isActive
						project {
							id
						}
						promoter {
							id
						}
					}
				}`
		}
	})
		.then(response => {
			return response.data.data.getPromotions
		});
}

export function isUserUsedFilledSurvey(userId) {
	return axios.get(`${URL}/promote-options/is_survey_used?userId=${userId}`);
}

export function promoteProject(projectId, optionType, applicableIds) {
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`
				mutation ($projectId: Int!, $optionType: PromotionOptionEnum!, $applicableIds: [Int]!) {
					createPromotion(projectId: $projectId, optionType: $optionType, applicableIds: $applicableIds) {
						id
						displayOrder
						createdDate
						expirationDate
					}
				}
				`,
			variables: {
				projectId: Number(projectId),
				optionType: optionType,
				applicableIds: applicableIds
			}
		}
	})
		.then(response => response.data);
}

export function setVideoLike(body) {
	return axios.post(`${URL}/video-resources`, body);
}

export function getVideoLikes(userId) {
	return axios.get(`${URL}/video-resources?userId=${userId}`);
}

export function setFaqLike(body) {
	return axios.post(`${URL}/faq-resources`, body);
}

export function getFaqLikes(userId) {
	return axios.get(`${URL}/faq-resources?userId=${userId}`);
}

// ----- Get Current Version Service

export function getCurrentVersion() {
	return axios.get(`${URL}/application-version`);
}

export function fetchAllFilterConfidentialities() {
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
					getConfidentialities {
							id
							label
							filterType
							description
					}
				}`
		}
	})
		.then(response => {
			let data = response.data.data.getConfidentialities
				.map(confidentiality => CONFIDENTIALITY_SERVICE.mapConfidentiality(confidentiality))
				.sort((first, second) => first.order - second.order);

			return {data}
		});
}

export function getAllCompanies(){
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
					searchCompanies(criteria: { pageable: { page: 0, size: 99999 } }) {
							id
							name
					}
				}`
		}
	})
		.then(response => {
			return {data: response.data.data.searchCompanies}
		});
}

export function getCompanies(criteria) {
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
						searchCompanies(
							criteria: {
									${criteria.isExactlyOneExpected ? `isExactlyOneExpected: ${criteria.isExactlyOneExpected}` : ''}
									${criteria.projectIsActive ? `projectIsActive: ${criteria.projectIsActive}` : ''}
									${criteria.projectConfidentialities ? `projectConfidentialities: [${criteria.projectConfidentialities}]` : ''}
									${criteria.projectStep ? `projectStep: ${criteria.projectStep}` : ''}
									${criteria.pageable ? `pageable: { page: ${criteria.pageable.page}, size: ${criteria.pageable.size}` : 'pageable: { page: 0, size: 100000000}'}
									${criteria.sort ? `sort: { by: ${criteria.sort.by}, order: ${criteria.sort.order} }` : ''}
									${criteria.name ? `name: ${criteria.name}` : ''}
									${criteria.id ? `id: ${criteria.id}` : ''}
							}
					) {
							id
							name
					}
				}`
		}
	})
		.then(response => {
			let data = response.data.data.searchCompanies;
			data = data.map((item) => {return {...item, filterType: 'companies'}})
			return {data}
		});
}

export function getTools(criteria) {
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
						searchQualificationAttributes(
							criteria: {
									${criteria.isExactlyOneExpected ? `isExactlyOneExpected: ${criteria.isExactlyOneExpected}` : ''}
									${criteria.hasSubmissions ? `hasSubmissions: ${criteria.hasSubmissions}` : ''}
									${criteria.approvalStatuses ? `approvalStatuses: [${criteria.approvalStatuses}]` : ''}
									${criteria.gopApprovalStatuses ? `gopApprovalStatuses: [${criteria.gopApprovalStatuses}]` : ''}
									${criteria.type ? `type: ${criteria.type}` : ''}
									${criteria.pageable ? `pageable: { page: ${criteria.pageable.page}, size: ${criteria.pageable.size}` : 'pageable: { page: 0, size: 100000000}'}
									${criteria.sort ? `sort: { by: ${criteria.sort.by}, order: ${criteria.sort.order} }` : ''}
									${criteria.name ? `name: ${criteria.name}` : ''}
									${criteria.text ? `text: ${criteria.text}` : ''}
									${criteria.ids ? `ids: [${criteria.ids}]` : ''}
							}
					) {
							id
							name
					}
				}`
		}
	})
		.then(response => {
			let data = response.data.data.searchQualificationAttributes;
			data = data.map((item) => {return {...item, filterType: 'tools'}})
			return {data}
		});
}

export function getCertificates(criteria) {
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
						searchQualificationAttributes(
							criteria: {
									${criteria.isExactlyOneExpected ? `isExactlyOneExpected: ${criteria.isExactlyOneExpected}` : ''}
									${criteria.hasSubmissions ? `hasSubmissions: ${criteria.hasSubmissions}` : ''}
									${criteria.approvalStatuses ? `approvalStatuses: [${criteria.approvalStatuses}]` : ''}
									${criteria.gopApprovalStatuses ? `gopApprovalStatuses: [${criteria.gopApprovalStatuses}]` : ''}
									${criteria.type ? `type: ${criteria.type}` : ''}
									${criteria.pageable ? `pageable: { page: ${criteria.pageable.page}, size: ${criteria.pageable.size}` : 'pageable: { page: 0, size: 100000000}'}
									${criteria.sort ? `sort: { by: ${criteria.sort.by}, order: ${criteria.sort.order} }` : ''}
									${criteria.name ? `name: ${criteria.name}` : ''}
									${criteria.text ? `text: ${criteria.text}` : ''}
									${criteria.ids ? `ids: [${criteria.ids}]` : ''}
							}
					) {
							id
							name
					}
				}`
		}
	})
		.then(response => {
			let data = response.data.data.searchQualificationAttributes;
			data = data.map((item) => {return {...item, filterType: 'certificates'}})
			return {data}
		});
}

export function getBusinessContactManager(criteria) {
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
						searchBusinessContactManagers(
							criteriaFilter: {
									${criteria.isExactlyOneExpected ? `isExactlyOneExpected: ${criteria.isExactlyOneExpected}` : ''}
									${criteria.projectIsActive ? `projectIsActive: ${criteria.projectIsActive}` : ''}
									${criteria.projectContactManagersNotNull ? `projectContactManagersNotNull: ${criteria.projectContactManagersNotNull}` : ''}
									${criteria.projectStep ? `projectStep: ${criteria.projectStep}` : ''}
									${criteria.pageable ? `pageable: { page: ${criteria.pageable.page}, size: ${criteria.pageable.size}` : 'pageable: { page: 0, size: 100000000}'}
									${criteria.sort ? `sort: { by: ${criteria.sort.by}, order: ${criteria.sort.order} }` : ''}
									${criteria.fullName ? `fullName: ${criteria.fullName}` : ''}
									${criteria.email ? `email: ${criteria.email}` : ''}
									${criteria.id ? `id: ${criteria.id}` : ''}
							}
					) {
							id
							fullName
					}
				}`
		}
	})
		.then(response => {
			let data = response.data.data.searchBusinessContactManagers;
			data = data.map(({ fullName, ...rest }) => ({
				...rest,
				name: fullName,
				filterType: 'businessContactManagers'
			}));
			return {data}
		});
}

export function getDeliveryContactManager(criteria) {
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
						searchDeliveryContactManagers(
							criteriaFilter: {
									${criteria.isExactlyOneExpected ? `isExactlyOneExpected: ${criteria.isExactlyOneExpected}` : ''}
									${criteria.projectIsActive ? `projectIsActive: ${criteria.projectIsActive}` : ''}
									${criteria.projectContactManagersNotNull ? `projectContactManagersNotNull: ${criteria.projectContactManagersNotNull}` : ''}
									${criteria.projectStep ? `projectStep: ${criteria.projectStep}` : ''}
									${criteria.pageable ? `pageable: { page: ${criteria.pageable.page}, size: ${criteria.pageable.size}` : 'pageable: { page: 0, size: 100000000}'}
									${criteria.sort ? `sort: { by: ${criteria.sort.by}, order: ${criteria.sort.order} }` : ''}
									${criteria.fullName ? `fullName: ${criteria.fullName}` : ''}
									${criteria.email ? `email: ${criteria.email}` : ''}
									${criteria.id ? `id: ${criteria.id}` : ''}
							}
					) {
							id
							fullName
					}
				}`
		}
	})
		.then(response => {
			let data = response.data.data.searchDeliveryContactManagers;
			data = data.map(({ fullName, ...rest }) => ({
				...rest,
				name: fullName,
				filterType: 'deliveryContactManagers'
			}));
			return {data}
		});
}

export function getDeliveryLocations(criteria) {
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
						searchDeliveryCountries(
							criteriaFilter: {
									${criteria.isExactlyOneExpected ? `isExactlyOneExpected: ${criteria.isExactlyOneExpected}` : ''}
									${criteria.text ? `text: ${criteria.text}` : ''}
									${criteria.ids ? `ids: [${criteria.ids}]` : ''}
									${criteria.pageable ? `pageable: { page: ${criteria.pageable.page}, size: ${criteria.pageable.size}` : 'pageable: { page: 0, size: 100000000}'}
									${criteria.sort ? `sort: { by: ${criteria.sort.by}, order: ${criteria.sort.order} }` : ''}
							}
					) {
							id
							code
							name
					}
				}`
		}
	})
		.then(response => {
			let data = response.data.data.searchDeliveryCountries;
			data = data.map((item) => {return {...item, filterType: 'teamLocations'}})
			return {data}
		});
}

export function getClientLocations(criteria) {
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
						searchCountries(
							criteriaFilter: {
									${criteria.isExactlyOneExpected ? `isExactlyOneExpected: ${criteria.isExactlyOneExpected}` : ''}
									${criteria.text ? `text: ${criteria.text}` : ''}
									${criteria.ids ? `ids: [${criteria.ids}]` : ''}
									${criteria.pageable ? `pageable: { page: ${criteria.pageable.page}, size: ${criteria.pageable.size}` : 'pageable: { page: 0, size: 100000000}'}
									${criteria.sort ? `sort: { by: ${criteria.sort.by}, order: ${criteria.sort.order} }` : ''}
							}
					) {
							id
							code
							name
					}
				}`
		}
	})
		.then(response => {
			let data = response.data.data.searchCountries;
			data = data.map((item) => {return {...item, filterType: 'clientLocations'}})
			return {data}
		});
}

export function searchIndustries(criteria) {
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
						searchIndustries(
							criteriaFilter: {
								  ${criteria.isExactlyOneExpected ? `isExactlyOneExpected: ${criteria.isExactlyOneExpected}` : ''}
									${criteria.text ? `text: ${criteria.text}` : ''}
									${criteria.name ? `name: ${criteria.name}` : ''}
									${criteria.ids ? `ids: [${criteria.ids}]` : ''}
									${criteria.pageable ? `pageable: { page: ${criteria.pageable.page}, size: ${criteria.pageable.size}` : 'pageable: { page: 0, size: 100000000}'}
									${criteria.sort ? `sort: { by: ${criteria.sort.by}, order: ${criteria.sort.order} }` : ''}
							}
					) {
							id
							translations {
									id
									name
									language {
											name
											id
									}
							}
							subIndustries {
									id
									translations {
											id
											name
											language {
													name
													id
											}
									}
							}
					}
				}`
		}
	})
		.then(response => {
			let data = response.data.data.searchIndustries;
			data = data.map((item) => {return {...item, filterType: 'industries'}})
			return {data}
		});
}

export function searchServiceLines(criteria) {
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
						searchServiceLines(
							criteriaFilter: {
							    ${criteria.isExactlyOneExpected ? `isExactlyOneExpected: ${criteria.isExactlyOneExpected}` : ''}
									${criteria.text ? `text: ${criteria.text}` : ''}
									${criteria.name ? `name: ${criteria.name}` : ''}
									${criteria.ids ? `ids: [${criteria.ids}]` : ''}
									${criteria.languageIds ? `languageIds: [${criteria.languageIds}]` : ''}
									${criteria.pageable ? `pageable: { page: ${criteria.pageable.page}, size: ${criteria.pageable.size}` : 'pageable: { page: 0, size: 100000000}'}
									${criteria.sort ? `sort: { by: ${criteria.sort.by}, order: ${criteria.sort.order} }` : ''}
							}
					) {
							id
							name
							subServiceLines {
									id
									name
									translations {
											id
											name
											language {
													id
													name
											}
									}
							}
							translations {
									id
									name
									language {
											id
											name
									}
							}
					}
				}`
		}
	})
		.then(response => {
			let data = response.data.data.searchServiceLines;
			data = data.map((item) => {return {...item, filterType: 'serviceLines'}})
			return {data}
		});
}

export function searchTechPractices(criteria) {
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
						searchTechPractices(
							criteriaFilter: {
							    ${criteria.isExactlyOneExpected ? `isExactlyOneExpected: ${criteria.isExactlyOneExpected}` : ''}
									${criteria.text ? `text: ${criteria.text}` : ''}
									${criteria.ids ? `ids: [${criteria.ids}]` : ''}
									${criteria.languageIds ? `languageIds: [${criteria.languageIds}]` : ''}
									${criteria.pageable ? `pageable: { page: ${criteria.pageable.page}, size: ${criteria.pageable.size}` : 'pageable: { page: 0, size: 100000000}'}
									${criteria.sort ? `sort: { by: ${criteria.sort.by}, order: ${criteria.sort.order} }` : ''}
							}
					) {
							id
							name
							translations {
									id
									name
									language {
											id
											name
									}
							}
							subTechPractices {
									id
									name
									translations {
											id
											name
											language {
													id
													name
											}
									}
							}
					}
				}`
		}
	})
		.then(response => {
			let data = response.data.data.searchTechPractices;
			data = data.map((item) => {return {...item, filterType: 'techPractices'}})
			return {data}
		});
}

export function searchFilterFtes(criteriaFilter) {
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
    			searchFtes(
							criteriaFilter: {
									${criteriaFilter.isExactlyOneExpected ? `isExactlyOneExpected: ${criteriaFilter.isExactlyOneExpected}` : ''}
									${criteriaFilter.pageable ? `pageable: { page: ${criteriaFilter.pageable.page}, size: ${criteriaFilter.pageable.size}` : 'pageable: { page: 0, size: 100000000}'}
									${criteriaFilter.sort ? `sort: { by: ${criteriaFilter.sort.by}, order: ${criteriaFilter.sort.order} }` : ''}
									${criteriaFilter.ids ? `ids: [${criteriaFilter.ids}]` : ''}
									${criteriaFilter.label ? `label: ${criteriaFilter.label}` : ''}
							}
					) {
       		 id,
        	 label
    			}
				}`
		}
	})
		.then(response => {
			let data = response.data.data.searchFtes;
			data = data.map(({ label, ...rest }) => ({
				...rest,
				name: label,
				filterType: 'ftes'
			}));
			return {data}
		});
}

export function searchFilterRevenues(criteriaFilter) {
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
    			searchRevenues(
							criteriaFilter: {
									${criteriaFilter.isExactlyOneExpected ? `isExactlyOneExpected: ${criteriaFilter.isExactlyOneExpected}` : ''}
									${criteriaFilter.pageable ? `pageable: { page: ${criteriaFilter.pageable.page}, size: ${criteriaFilter.pageable.size}` : 'pageable: { page: 0, size: 100000000}'}
									${criteriaFilter.sort ? `sort: { by: ${criteriaFilter.sort.by}, order: ${criteriaFilter.sort.order} }` : ''}
									${criteriaFilter.ids ? `ids: [${criteriaFilter.ids}]` : ''}
									${criteriaFilter.label ? `label: ${criteriaFilter.label}` : ''}
							}
					) {
       		 id,
        	 label
    			}
				}`
		}
	})
		.then(response => {
			let data = response.data.data.searchRevenues;
			data = data.map(({ label, ...rest }) => ({
				...rest,
				name: label,
				filterType: 'revenues'
			}));
			return {data}
		});
}

// ------ Search Filters Services

export function fetchFilters() {
	return new Promise((resolve, reject) => {
		Promise.all([
			searchIndustries({}),
			searchTechPractices({}),
			searchServiceLines({}),
			searchFilterFtes({}),
			fetchAllFilterConfidentialities(),
			searchFilterRevenues({}),
			getDeliveryLocations({}),
			getClientLocations({}),
			getBusinessContactManager({}),
			getDeliveryContactManager({}),
			axios.get(`${URL}/users/search-filters/reference-owner`),
			getCompanies({projectConfidentialities: ['EXTERNAL', 'INTERNAL']}),
			getTools({approvalStatuses: ['APPROVED'], type: 'TOOL'}),
			getCertificates({approvalStatuses: ['APPROVED'], type: 'CERTIFICATION'}),
		]).then(resolve)
			.catch(reject);
	});
}

export function changeProjectStatus(params) {
	return axios.post(`${URL}/projects/statuses`, {...params});
}

export function setSubscription(subscription) {
	return axios.post(`${URL}/subscriptions`, subscription);
}

export function setSubscriptionActivationStatus(subscriptionId, status) {
	return axios.put(`${URL}/subscriptions?subscriptionId=${subscriptionId}&isActive=${status}`);
}

export function deleteSubscription(id) {
	return axios.delete(`${URL}/subscriptions?subscriptionId=${id}`);
}

export function getSubscribers(criteria) {
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
					searchSubscriptions(
							criteria: {
									${criteria.pageable ? `pageable: { page: ${criteria.pageable.page}, size: ${criteria.pageable.size}` : 'pageable: { page: 0, size: 100000000}'}
									${criteria.isExactlyOneExpected ? `isExactlyOneExpected: ${criteria.isExactlyOneExpected}` : ''}
									${criteria.ids ? `ids: [${criteria.ids}]` : ''}
									${criteria.userId ? `userId: ${criteria.userId}` : ''}
									${criteria.text ? `text: "${criteria.text}"` : ''}
									${criteria.subscriptionTypes ? `subscriptionTypes: [${criteria.subscriptionTypes}]` : ''}
									${criteria.sort ? `sort: { by: "${criteria.sort.by}", order: "${criteria.sort.order}" }` : ''}
							}
					) {
							user {
									fullName
							}
					}
				}`
		}
	})
		.then(response => {
			return response.data.data.searchSubscriptions
		});
}

export function getSubscriptions(criteria) {
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
					searchSubscriptions(
							criteria: {
									${criteria.pageable ? `pageable: { page: ${criteria.pageable.page}, size: ${criteria.pageable.size}` : 'pageable: { page: 0, size: 100000000}'}
									${criteria.isExactlyOneExpected ? `isExactlyOneExpected: ${criteria.isExactlyOneExpected}` : ''}
									${criteria.ids ? `ids: [${criteria.ids}]` : ''}
									${criteria.userId ? `userId: ${criteria.userId}` : ''}
									${criteria.text ? `text: "${criteria.text}"` : ''}
									${criteria.subscriptionTypes ? `subscriptionTypes: [${criteria.subscriptionTypes}]` : ''}
									${criteria.sort ? `sort: { by: "${criteria.sort.by}", order: "${criteria.sort.order}" }` : ''}
							}
					) {
							id
							title
							comment
							isActive
							periodType
							createdDate
							clients
							confidentialities
							revenues
							ftes
							clientLocations
							deliveryCountries
							tools
							certifications
							referenceOwners
							businessManagers
							deliveryManagers
							user {
									id
							}
							industries {
									id
									subIds
							}
							serviceLines {
									id
									subIds
							}
							techPractices {
									id
									subIds
							}
							subscriptionType
							subscriptionHistories {
									id
									title
									createdDate
							}
					}
				}`
		}
	})
		.then(response => {
			return response.data.data.searchSubscriptions
		});
}

export function getInitialSubmissions(criteria) {
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
					getInitialSubmissions(
							criteria: {
									${criteria.pageable ? `pageable: { page: ${criteria.pageable.page}, size: ${criteria.pageable.size}` : 'pageable: { page: 0, size: 100000000}'}
									${criteria.isExactlyOneExpected ? `isExactlyOneExpected: ${criteria.isExactlyOneExpected}` : ''}
									${criteria.projectIds ? `projectIds: [${criteria.projectIds}]` : ''}
							}
					) {
							referenceTitle
							clientContext
							aboutTheClient
							solution
							akkodisChallenge
							clientResult
							akkodisResult
							project {
									id
							}
							language {
									id
									name
							}
					}
				}`
		}
	})
		.then(response => {
			return response.data.data.getInitialSubmissions[0]
		});
}

export function getProject(criteria) {
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
					getProjects(
							criteriaFilter: {
									pageable: { page: 0, size: 100000000}
									isActive: ${criteria.isActive}
									currentProjectSteps: ${criteria.step ? `[${criteria.step}]` : null}
									isExactlyOneExpected: true
									projectId: ${criteria.projectId}
									ownerId: ${criteria.ownerId ? criteria.ownerId : null}
							}
					) {
						        id
										industryReference {
												industry {
														id
														translations {
																id
																language {
																		id
																		name
																}
																name
														}
												}
												subIndustries {
														id
														name
														translations {
																name
																language {
																		id
																		name
																}
																id
														}
												}
										}
										views
										translations {
											  aboutTheClient
												referenceTitle
												language {
														name
														id
												}
												akkodisChallenge
												id
												clientResult
												clientContext
												akkodisResult
												solution
												isTranslated
												isValidated
												validationDate
												validatorId
										}
										submission {
												id
												language {
														id
														name
												}
										}
										management {
												firstDatePublish
												datePublish
										}
										projectImages {
												type
												objectUrl
										}
										isNewBusiness
										dateFrom
										dateTo
										isOngoing
										duration
										serviceLineReferences {
												serviceLine {
														id
														name
														translations {
																id
																name
																description
																language {
																		id
																		name
																}
														}
												}
												subServiceLines {
														id
														name
														translations {
																id
																name
																language {
																		id
																		name
																}
														}
												}
										}
										techPracticeReferences {
												id
												techPractice {
														id
														name
														translations {
																id
																name
																language {
																		id
																		name
																}
														}
												}
												subTechPractices {
														id
														name
														title
														translations {
																id
																name
																language {
																		id
																		name
																}
														}
												}
										}
										testimonials {
												id
												translations {
														id
														authorName
														jobTitle
														text
														isValidated
														language {
																id
																name
														}
												}
										}
										links {
												id
												url
												translations {
														id
														keyword
														language {
																id
																name
														}
												}
										}
										company {
												id
												name
										}
										owner {
												id
												username
												email
												fullName
										}
										businessContactManager {
												id
												fullName
												email
										}
										deliveryContactManager {
												id
												fullName
												email
										}
										confidentiality
										revenue {
												id
												label
										}
										fte {
												id
												label
										}
										qualificationAttributes {
												id
												name
												type
										}
										deliveryLocations {
												id
												name
										}
										clientLocations {
												id
												name
										}
										currentProjectStep
										projectImages {
												type
												objectUrl
												id
												objectKey
							}
					}
				}`
		}
	})
		.then(response => {
			return response.data.data.getProjects[0]
		});
}

export function getProjectsCount(params) {
	let criteria = router.currentRoute.query;

	if (params) {
		const criteriaParams = Object.fromEntries(new URLSearchParams(params));
		criteria.saverId = criteriaParams.saverId;
		criteria.language = criteriaParams.language;
		criteria.size = Number(criteriaParams.size);
		criteria.page = Number(criteriaParams.page);

		if (criteriaParams.text) {
			criteria.text = criteriaParams.text
		}

		if (criteriaParams.industries) {
			criteria.industries = criteriaParams.industries
			criteria.subIndustries = criteriaParams.subIndustries
		}

		if (criteriaParams.techPractices) {
			criteria.techPractices = criteriaParams.techPractices
			criteria.subTechPractices = criteriaParams.subTechPractices
		}

		if (criteriaParams.serviceLines) {
			criteria.serviceLines = criteriaParams.serviceLines
			criteria.subServiceLines = criteriaParams.subServiceLines
		}

	}

	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
					getProjects(
							criteriaFilter: {
									isActive: true
									pageable: { page: 0, size: 100000000}
									currentProjectSteps: PUBLISHED
									isExactlyOneExpected: false
									publicationDates: ${criteria.publicationDates ?  `[${criteria.publicationDates.split(",").map(date => `"${date}"`)}]` : null}
									text: ${criteria.text ? `"${criteria.text}"` : null}
									language: ${criteria.language ? criteria.language : "EN"}
									deliveryLocations: ${criteria.teamLocations ? `[${criteria.teamLocations}]` : null}
									clientLocations: ${criteria.clientLocations ? `[${criteria.clientLocations}]` : null}
									industries: ${criteria.industries ? `[${criteria.industries}]` : null}
									subIndustries: ${criteria.subIndustries ? `[${criteria.subIndustries}]` : null}
									serviceLines: ${criteria.serviceLines ? `[${criteria.serviceLines}]` : null}
									subServiceLines: ${criteria.subServiceLines ? `[${criteria.subServiceLines}]` : null}
									techPractices: ${criteria.techPractices ? `[${criteria.techPractices}]` : null}
									subTechPractices: ${criteria.subTechPractices ? `[${criteria.subTechPractices}]` : null}
									ftes: ${criteria.ftes ? `[${criteria.ftes}]` : null}
									confidentialities: ${criteria.confidentialities ? `[${criteria.confidentialities}]` : null}
									tools: ${criteria.tools ? `[${criteria.tools}]` : null}
									certifications: ${criteria.certifications ? `[${criteria.certifications}]` : null}
									revenues: ${criteria.revenues ? `[${criteria.revenues}]` : null}
									referenceOwners: ${criteria.referenceOwners ? `[${criteria.referenceOwners}]` : null}
									businessContactManagers: ${criteria.businessContactManagers ? `[${criteria.businessContactManagers}]` : null}
									deliveryContactManagers: ${criteria.deliveryContactManagers ? `[${criteria.deliveryContactManagers}]` : null}
									companies: ${criteria.companies ? `[${criteria.companies}]` : null}
									saverId: ${criteria.saverId ? `${Number(criteria.saverId)}` : null}
							}
					) {
							id
					}
				}`
		}
	})
		.then(response => {
			return response.data.data.getProjects
		});
}

export function fetchProjects(params) {
	let by;
	let order;

	if (params) {
		// criteria.language = params.language;
		[by, order] = params.sort ? params.sort.split(",") : 'management_firstDatePublish,DESC'.split(",");
	}

	let text
	if (params.text) {
		text = encodeURIComponent(params.text)
	}

	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
					getProjects(
							criteriaFilter: {
									${params.isActive ? `isActive: ${params.isActive}` : 'isActive: true'}
									pageable: { page: ${params?.page ? Number(params.page) : 0}, size: ${params?.size ? Number(params.size) : 16} }
									${params.currentProjectSteps ? `currentProjectSteps: [${params.currentProjectSteps}]` : 'currentProjectSteps: [PUBLISHED]'}
									${params.isExactlyOneExpected ? `isExactlyOneExpected: "${params.isExactlyOneExpected}"` : 'isExactlyOneExpected: false'}
									${params.publicationDates ? `publicationDates: [${params.publicationDates.split(",").map(date => `"${date}"`)}]` : ''}
									sort: { by: "${by}", order: "${order}" }
									${text ? `text: "${text}"` : ''}
									${params.language ? `language: ${params.language}` : ''}
									${params.deliveryLocations ? `deliveryLocations: [${params.deliveryLocations.split(",")}]` : ''}
									${params.clientCountries ? `clientLocations: [${params.clientCountries.split(",")}]` : ''}
									${params.industries ? `industries: [${params.industries.split(",")}]` : ''}
									${params.subIndustries ? `subIndustries: [${params.subIndustries.split(",")}]` : ''}
									${params.serviceLines ? `serviceLines: [${params.serviceLines.split(",")}]` : ''}
									${params.subServiceLines ? `subServiceLines: [${params.subServiceLines.split(",")}]` : ''}
									${params.techPractices ? `techPractices: [${params.techPractices.split(",")}]` : ''}
									${params.subTechPractices ? `subTechPractices: [${params.subTechPractices.split(",")}]` : ''}
									${params.ftes ? `ftes: [${params.ftes.split(",")}]` : ''}
									${params.confidentialities ? `confidentialities: [${params.confidentialities.split(",")}]` : ''}
									${params.tools ? `tools: [${params.tools.split(",")}]` : ''}
									${params.certificates ? `certifications: [${params.certificates.split(",")}]` : ''}
									${params.revenues ? `revenues: [${params.revenues.split(",")}]` : ''}
									${params.referenceOwners ? `referenceOwners: [${params.referenceOwners.split(",")}]` : ''}
									${params.businessContactManagers ? `businessContactManagers: [${params.businessContactManagers.split(",")}]` : ''}
									${params.deliveryContactManagers ? `deliveryContactManagers: [${params.deliveryContactManagers.split(",")}]` : ''}
									${params.companies ? `companies: [${params.companies.split(",")}]` : ''}
									${params.saverId ? `saverId: ${params.saverId}` : ''}
									${params.ownerId ? `ownerId: ${params.ownerId}` : ''}
							}
					) {
							id
							industryReference {
									industry {
											id
											translations {
													id
													language {
															id
															name
													}
													name
											}
									}
									subIndustries {
											id
											translations {
												name
												language {
													id
													name
											}
										}
									}
							}
							views
							translations {
									aboutTheClient
									referenceTitle
									language {
											name
											id
									}
									isValidated
							}
							submission {
									id
									language {
											id
											name
									}
							}
							management {
									datePublish
							}
							savers {
									id
							}
							projectImages {
									type
									objectUrl
							}
							currentProjectStep
					}
				}`
		}
	})
		.then(response => {
			return response.data.data.getProjects
		});
}

export function fetchProjectsCount(params) {
	let by;
	let order;
	let parameters = {...params}

	if (parameters) {
		[by, order] = parameters.sort ? parameters.sort.split(",") : 'management_firstDatePublish,DESC'.split(",");
		parameters.page = 0;
		parameters.size = 9999999999
	}

	let text
	if (params.text) {
		text = encodeURIComponent(params.text)
	}

	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
					getProjects(
							criteriaFilter: {
									${parameters.isActive ? `isActive: ${parameters.isActive}` : 'isActive: true'}

									${parameters.currentProjectSteps ? `currentProjectSteps: [${parameters.currentProjectSteps}]` : 'currentProjectSteps: [PUBLISHED]'}
									${parameters.isExactlyOneExpected ? `isExactlyOneExpected: "${parameters.isExactlyOneExpected}"` : 'isExactlyOneExpected: false'}
									${parameters.publicationDates ? `publicationDates: [${parameters.publicationDates.split(",").map(date => `"${date}"`)}]` : ''}
									sort: { by: "${by}", order: "${order}" }
									${text ? `text: "${text}"` : ''}
									${parameters.language ? `language: ${parameters.language}` : ''}
									${parameters.deliveryLocations ? `deliveryLocations: [${parameters.deliveryLocations.split(",")}]` : ''}
									${parameters.clientCountries ? `clientLocations: [${parameters.clientCountries.split(",")}]` : ''}
									${parameters.industries ? `industries: [${parameters.industries.split(",")}]` : ''}
									${parameters.subIndustries ? `subIndustries: [${parameters.subIndustries.split(",")}]` : ''}
									${parameters.serviceLines ? `serviceLines: [${parameters.serviceLines.split(",")}]` : ''}
									${parameters.subServiceLines ? `subServiceLines: [${parameters.subServiceLines.split(",")}]` : ''}
									${parameters.techPractices ? `techPractices: [${parameters.techPractices.split(",")}]` : ''}
									${parameters.subTechPractices ? `subTechPractices: [${parameters.subTechPractices.split(",")}]` : ''}
									${parameters.ftes ? `ftes: [${parameters.ftes.split(",")}]` : ''}
									${parameters.confidentialities ? `confidentialities: [${parameters.confidentialities.split(",")}]` : ''}
									${parameters.tools ? `tools: [${parameters.tools.split(",")}]` : ''}
									${parameters.certificates ? `certifications: [${parameters.certificates.split(",")}]` : ''}
									${parameters.revenues ? `revenues: [${parameters.revenues.split(",")}]` : ''}
									${parameters.referenceOwners ? `referenceOwners: [${parameters.referenceOwners.split(",")}]` : ''}
									${parameters.businessContactManagers ? `businessContactManagers: [${parameters.businessContactManagers.split(",")}]` : ''}
									${parameters.deliveryContactManagers ? `deliveryContactManagers: [${parameters.deliveryContactManagers.split(",")}]` : ''}
									${parameters.companies ? `companies: [${parameters.companies.split(",")}]` : ''}
									${parameters.saverId ? `saverId: ${parameters.saverId}` : ''}
									${params.ownerId ? `ownerId: ${params.ownerId}` : ''}
							}
					) {
							id
					}
				}`
		}
	})
		.then(response => {
			return response.data.data.getProjects
		});
}

export function searchPageProjects(params) {
	let criteria = router.currentRoute.query;
	let by;
	let order;

	if (params) {
		const criteriaParams = Object.fromEntries(new URLSearchParams(params));
		criteria.saverId = criteriaParams.saverId;
		criteria.language = criteriaParams.language;
		[by, order] = criteriaParams.sort ? criteriaParams.sort.split(",") : 'management_firstDatePublish,DESC'.split(",");
		criteria.size = Number(criteriaParams.size);
		criteria.page = Number(criteriaParams.page);

		criteria.text = criteriaParams.text  || ''

		criteria.industries = criteriaParams.industries || ''
		criteria.subIndustries = criteriaParams.subIndustries || ''

		criteria.techPractices = criteriaParams.techPractices || ''
		criteria.subTechPractices = criteriaParams.subTechPractices || ''

		criteria.serviceLines = criteriaParams.serviceLines || ''
		criteria.subServiceLines = criteriaParams.subServiceLines || ''

	} else {
		if (router.currentRoute.query.sort === undefined) {
			[by, order] = ['management_firstDatePublish','DESC']
		} else {
			[by, order] = router.currentRoute.query.sort.split(",");
		}
	}


	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
					getProjects(
							criteriaFilter: {
									isActive: true
									pageable: { page: ${criteria.page ? criteria.page : 0}, size: ${criteria.size ? criteria.size : 27} }
									currentProjectSteps: PUBLISHED
									isExactlyOneExpected: false
									publicationDates: ${criteria.publicationDates ?  `[${criteria.publicationDates.split(",").map(date => `"${date}"`)}]` : null}
									sort: { by: "${by}", order: "${order}" }
									text: ${criteria.text ? `"${criteria.text}"` : null}
									language: ${criteria.language ? criteria.language : "EN"}
									deliveryLocations: ${criteria.teamLocations ? `[${criteria.teamLocations}]` : null}
									clientLocations: ${criteria.clientLocations ? `[${criteria.clientLocations}]` : null}
									industries: ${criteria.industries ? `[${criteria.industries}]` : null}
									subIndustries: ${criteria.subIndustries ? `[${criteria.subIndustries}]` : null}
									serviceLines: ${criteria.serviceLines ? `[${criteria.serviceLines}]` : null}
									subServiceLines: ${criteria.subServiceLines ? `[${criteria.subServiceLines}]` : null}
									techPractices: ${criteria.techPractices ? `[${criteria.techPractices}]` : null}
									subTechPractices: ${criteria.subTechPractices ? `[${criteria.subTechPractices}]` : null}
									ftes: ${criteria.ftes ? `[${criteria.ftes}]` : null}
									confidentialities: ${criteria.confidentialities ? `[${criteria.confidentialities}]` : null}
									tools: ${criteria.tools ? `[${criteria.tools}]` : null}
									certifications: ${criteria.certifications ? `[${criteria.certifications}]` : null}
									revenues: ${criteria.revenues ? `[${criteria.revenues}]` : null}
									referenceOwners: ${criteria.referenceOwners ? `[${criteria.referenceOwners}]` : null}
									businessContactManagers: ${criteria.businessContactManagers ? `[${criteria.businessContactManagers}]` : null}
									deliveryContactManagers: ${criteria.deliveryContactManagers ? `[${criteria.deliveryContactManagers}]` : null}
									companies: ${criteria.companies ? `[${criteria.companies}]` : null}
									saverId: ${criteria.saverId ? `${Number(criteria.saverId)}` : null}
							}
					) {
							id
							industryReference {
									industry {
											id
											translations {
													id
													language {
															id
															name
													}
													name
											}
									}
									subIndustries {
											id
											translations {
												name
												language {
													id
													name
											}
										}
									}
							}
							views
							translations {
									aboutTheClient
									referenceTitle
									language {
											name
											id
									}
									isValidated
							}
							submission {
									id
									language {
											id
											name
									}
							}
							management {
									firstDatePublish
									datePublish
							}
							savers {
									id
							}
							projectImages {
									type
									objectUrl
							}
							currentProjectStep
					}
				}`
		}
	})
		.then(response => {
			return response.data.data.getProjects
		});
}

export function getTopContributors(months, countContributors) {
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
					getTopContributors(months: ${months}, countContributors: ${countContributors}) {
						id
						fullName
						email
						projects {
							id
							views
							industryReference {
								industry {
									id
									translations {
										id
										language {
											id
											name
										}
										name
									}
								}
								subIndustries {
									id
									translations {
										name
										language {
											id
											name
										}
									}
								}
							}
							submission {
								id
								language {
									id
									name
								}
							}
							translations {
								aboutTheClient
								referenceTitle
								language {
									name
									id
								}
								isValidated
							}
							management {
								firstDatePublish
								datePublish
							}
							projectImages {
								type
								objectUrl
							}
							translations {
								id
							}
						}
					}
				}`
		}
	})
		.then(response => {
			return response.data.data.getTopContributors
		});
}


// ------ User Saved Projects Services

export function fetchUserSavedProjects(params) {
	return axios.get(`${URL}/${VERSION}/main/projects/search${params}`);
}

export function fetchUserSavedProjectsIds(userId) {
	return axios.get(`${URL}/old-projects/get-all-by-user/${userId}`);
}

export function removeProjectFromSaved(userId, projectId) {
	return axios.post(`${URL}/old-projects/remove?userId=${userId}&projectId=${projectId}`)
}

export function addProjectToSaved(userId, projectId) {
	return axios.post(`${URL}/old-projects/add?userId=${userId}&projectId=${projectId}`)
}

// ------ Projects Crud Services
export function getServices() {
	return axios.get(`${URL}/old-service-lines/get-all`);
}

export function getTechnologies() {
	return axios.get(`${URL}/old-tech-practices/get-all-tech-practices-new`);
}

export function getIndustries() {
	return axios.get(`${URL}/old-industries/get-all-form-industries`);
}

export function uploadLandingPageBackgroundImage(files, type) {
	return axios.put(`${URL}/main/images/landing-page/upload-background-image?type=${type}`,
		files,  {
		headers: {
			"Content-Type": "multipart/form-data",
		}
	});
}

export function getProjectForValidation(projectId, langId) {
	return axios.get(`${URL}/old-projects/${projectId}/get-validation-for-language?lang=${langId}`);
}

export function validateProjectTranslation(projectId, data) {
	return axios.put(`${URL}/old-projects/${projectId}/validate-language-for-project`, data);
}

// ------- User Dashboard Services

export async function archiveDraft(projectId) {
	return axios.post(`${URL}/old-projects/archive-draft/${projectId}`);
}

export function unarchiveDraft(projectId) {
	return axios.post(`${URL}/old-projects/unarchive-draft/${projectId}`);
}

export function getUserDraftProjectsCount(userId, isActiveDrafts) {
	return axios.get(`${URL}/old-projects/get-user-drafts-count?userId=${userId}&isActive=${isActiveDrafts}`)
}

// ---- Project Services

export function fetchMyProjects(userId, filters) {
	return axios.get(`${URL}/old-projects/users/${userId}/search${filters}`);
}

export function fetchMySteps(userId) {
	return axios.get(`${URL}/old-projects/project-steps-with-count/${userId}`);
}

export function fetchProjectForExportById(id, type) {
	return axios.get(`${URL}/old-projects/export/${id}?type=${type}`);
}

export function unarchive(id) {
	return axios.patch(`${URL}/old-management/projects/unarchive/${id}`);
}

// ------ Steps

export function getAllSteps() {
	return axios.get(`${URL}/old-management/projects/project-steps`);
}

export function getManagementProjects(filters) {
	return axios.get(`${URL}/old-management/projects/search${filters}`)
}

export function getUsersByRoleId(roleId) {
	return axios.get(`${URL}/users/management/get-assignable-users-by-roleId?roleId=${roleId}`)
}

export function exportProjectsFromStepToExcel(stepsIds, showActiveProjects) {
	return axios.get(`${URL}/old-management/projects/export-multiple-projects-to-excel?submissionSteps=${stepsIds}&isActive=${showActiveProjects}`, {responseType: 'arraybuffer'});
}

export function invalidate(projectId, language) {
	return axios.patch(`${URL}/old-management/projects/invalidate/${projectId}?language=${language}`);
}

export function archive(projectId) {
	return axios.delete(`${URL}/old-management/projects/${projectId}/archive`);
}

export function deleteArchivedDraft(projectId) {
	return axios.delete(`${URL}/new-project/${projectId}`)
}

export function resendEmailForReferenceOwnerApproval(projectId) {
	return axios.post(`${URL}/new-project/${projectId}/resend-reference-owner-approval-email`)
}

export function getLatestProjects(projectsNumber) {
	return axios.get(`${URL}/old-projects/get-newest?count=${projectsNumber}`);
}

export function getLatestProjects2(projectsNumber, language) {
	return axios.get(`${URL}/old-projects/search-get-newest?page=0&count=${projectsNumber}&lang=&lang=${language}&size=5`);
}

export function getProjectsByContinent(continents, page) {
	let url = `${URL}/old-projects/find-by-continent?size=5&page=${page ? page : 0}&sort=management_firstDatePublish,id,desc&continents=${continents}`;

	return axios.get(url);
}

export function getMostViewedProjectForTheLastTwoMonths() {
	let url = `${URL}/old-projects/get-newest-project`;

	return axios.get(url);
}

export function getRandomProjects(criteria) {
	return axios.post(`${URL}/old-projects/get-random`, criteria);
}

export function unifyCatch(reject, message) {
	return (err) => {
		console.error(message);
		console.error(err.response.data.message);
		reject(err);
	};
}
export function createDownloadHistory(project, documentType){
	return axios.post(`${URL}/old-downloads/create-history-entry`, {project, documentType});
}

export function getDraftProject(id) {
	return axios.get(`${URL}/new-project/${id}?id=${id}&step=DRAFT`);
	// return axios.get(`${URL}/new-project/${id}?id=${id}&step=PUBLISHED`);
}

export function submitProject(project, action, user='USER') {//action='SAVE' || 'UPDATE'; user='USER' || 'MANAGEMENT'
	return axios.post(`${URL}/new-project?action=${action}&type=${user}`, project, {headers: {}});
}

export function submitSurvey(surveyData, userId, surveyId){
	return axios.post(`${URL}/surveys`, {submittedInfo: surveyData, user:{id: userId}, surveyId:surveyId}, {headers: {}});
}

export function getAllSurveys(){
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
						getAllSurveys {
							id
							surveyName
							isActive
							createdDate
							drcSurveyQuestions {
									id,
									question
							}
					}
				}`
		}
	})
	.then(response => {
		return response.data.data.getAllSurveys;
	});
}

export function activateSurvey(id){
	return axios.post(`${URL}/management/surveys/activate?surveyId=${id}`);
}

export function deleteSurvey(id){
	return axios.delete(`${URL}/management/surveys?surveyId=${id}`);
}

export function getSurveyById(id){
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
					getSurveyById(surveyId: ${id}) {
						id
						surveyName
						isActive
						createdDate
						drcSurveyQuestions {
							id
							question
							answerType
							drcSurveyAnswers {
								id
								answer
							}
						}
					}
				}`
		}
	})
		.then(response => {
			return response.data.data.getSurveyById;
		});
}

export function saveSurvey(survey){
	return axios.post(`${URL}/management/surveys/set`, survey);
}

export function getAllVideos(){
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
					getAllResourceVideos {
						 	id
							displayOrder
							objectUrl
							isHidden
							translations {
								id
								title
								description
								language {
										id
										name
								}
						}
					}
				}`
		}
	})
	.then(response => {
		return response.data.data.getAllResourceVideos;
	});
}

export function setNewVideosOrder(videos){
	return axios.put(`${URL}/video-resources/reorder`, videos);
}

export function editVideo(video) {
	return axios.put(`${URL}/video-resources/update`, video, {headers: {}});
}

export function deleteVideo(id){
	return axios.delete(`${URL}/video-resources?id=${id}`);
}

export function createVideo(data){
	return axios.post(`${URL}/video-resources/upload`, data, {headers: {}});
}

export function updateVideo(data){
	return axios.put(`${URL}/video-resources/update`, data, {headers: {}});
}

export function getVideoById(id){
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
					getResourceVideoById(id: ${id}) {
						id
						displayOrder
						objectUrl
						objectKey
						isHidden
						translations {
								id
								title
								description
								isTranslated
								language {
										id
										name
								}
						}
				}
				}`
		}
	}).then(res => {
		return res.data.data.getResourceVideoById;
	})
}

export function getNotHiddenVideos(){
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
					getAllNotHiddenResourceVideos {
						id
						displayOrder
						objectUrl
						objectKey
						isHidden
						translations {
							id
							title
							description
							isTranslated
							language {
								id
								name
							}
						}
				}
				}`
		}
	}).then(res => {
		return res.data.data.getAllNotHiddenResourceVideos;
	})
}

export function getAllResourceFiles(){
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
					getAllResourceFiles {
						id
						displayOrder
						objectUrl
						isHidden
						translations {
							id
							title
							description
							language {
									id
									name
							}
						}
					}
				}`
		}
	})
		.then(response => {
			return response.data.data.getAllResourceFiles;
		});
}
export function setNewResourceFilesOrder(videos){
	return axios.put(`${URL}/file-resources/reorder`, videos);
}

export function editResourceFile(video) {
	return axios.put(`${URL}/file-resources/update`, video, {headers: {}});
}

export function deleteResourceFile(id){
	return axios.delete(`${URL}/file-resources?id=${id}`);
}

export function createResourceFile(data){
	return axios.post(`${URL}/file-resources/upload`, data, {headers: {}});
}

export function updateResourceFile(data){
	return axios.put(`${URL}/file-resources/update`, data, {headers: {}});
}

export function getResourceFileById(id){
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
					getResourceFileById(id: ${id}) {
						id
						displayOrder
						objectUrl
						objectKey
						isHidden
						translations {
								id
								title
								description
								isTranslated
								language {
										id
										name
								}
						}
				}
				}`
		}
	}).then(res => {
		return res.data.data.getResourceFileById;
	})
}

export function getNotHiddenResourceFiles(){
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
					getAllNotHiddenResourceFiles {
						id
						displayOrder
						objectUrl
						objectKey
						isHidden
						translations {
							id
							title
							description
							isTranslated
							language {
								id
								name
							}
						}
				}
				}`
		}
	}).then(res => {
		return res.data.data.getAllNotHiddenResourceFiles;
	})
}

export function getAllPromotions(){
	return axios({
		url: GRAPHQL_ENDPOINT,
		method: METHOD_TYPE.POST,
		data: {
			query:
				`{
					getPromotions(criteria: { pageable: { page: 0, size: 999 } }) {
						id
						displayOrder
						isActive
						createdDate
						expirationDate

						project{
							id,
							currentProjectStep
							management{
								firstDatePublish
								datePublish
							},
							views,
							projectImages{
								objectUrl,
								type
							},
							translations{
								language{
									name,
									id
								},
								referenceTitle,
								aboutTheClient,
								isValidated
							},
							industryReference{
								industry{
									translations{
									 language{
											name
										},
										name
									}
								}
								subIndustries {
									id
									translations {
										name
										language {
											id
											name
										}
									}
								}
							}
						}

				}
				}`
		}
	}).then(res => {
		return res.data.data.getPromotions;
	})
}

export function setNewPromotionsOrder(promotions){
	return axios.put(`${URL}/promotions/reorder`, promotions);
}

export function setNewPromotionDate(promotion){
	return axios.put(`${URL}/promotions/expiration-date`, promotion);
}

export function deletePromotion(id){
	return axios.delete(`${URL}/promotions?promotionId=${id}`);
}

export function disableEmailOnPublish(projectId) {
	return axios.put(`${URL}/new-project/should-send-publish-email?id=${projectId}&shouldSendEmail=false`);
}

export function fetchGeneralReportData(startDate, endDate) {
	return axios.get(`${URL}/statistics/period-statistic?startDate=${startDate}&endDate=${endDate}`)
}
